import { AdminApiError, type AdminClient, type GameCatalogEntry } from '@apx/admin-sdk';
import { confirmModal, promptModal } from '../modal.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import { prettyGame } from '../games.js';

export interface ProviderGamesPageDeps {
  client: AdminClient;
  /** Designated platform integrator used as the canonical config-bearing row.
   *  Per-integrator overrides are not exposed in the admin UI — apx
   *  owns the configs and they apply to every integrator. */
  platformTenantId: string;
}

/**
 * Provider-scope game list. Shows every registered game with a global on/off
 * toggle + a provider-wide maintenance switch (both apply to every integrator),
 * and a drill-in to the per-game config (timing, RNG, jackpot, rules,
 * leaderboard, big-winner, live inspector). Configs are global — this page
 * lives outside the integrator URL scope.
 */
export async function renderProviderGamesPage(
  root: HTMLElement,
  deps: ProviderGamesPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Games</h1>
    <p class="page-sub">Global game library. RNG, timing, jackpot, rules, leaderboard, and big-winner are configured once here and apply to <strong>every integrator</strong>. Toggle a game <strong>off</strong> to pull it from every integrator (sessions are rejected); use <strong>maintenance</strong> for a temporary "be right back" without disabling it.</p>
    <div class="card">
      <h2 style="margin-top:0">Registered games</h2>
      <div id="pg-list">${skeletonTable({ rows: 3, columns: 4 })}</div>
    </div>
    ${STYLE}
  `;
  const listEl = root.querySelector<HTMLElement>('#pg-list')!;

  async function refresh(): Promise<void> {
    try {
      const { games } = await deps.client.listGameCatalog(deps.platformTenantId);
      if (games.length === 0) {
        listEl.innerHTML = '<p class="muted">No games registered yet.</p>';
        return;
      }
      listEl.innerHTML = `<div class="game-grid">${games.map(card).join('')}</div>`;
      bindIconFallbacks();
      bindActions(games);
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function bindActions(games: GameCatalogEntry[]): void {
    const byId = new Map(games.map((g) => [g.gameId, g]));

    listEl.querySelectorAll<HTMLButtonElement>('button[data-toggle-enabled]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const g = byId.get(btn.dataset.toggleEnabled!);
        if (!g) return;
        const next = !g.registryEnabled;
        if (!next) {
          const ok = await confirmModal({
            title: `Disable ${g.gameId} for all integrators?`,
            body: 'New sessions for this game will be rejected across every integrator. In-flight rounds settle normally. Re-enable any time.',
            confirmLabel: 'Disable',
            danger: true,
          });
          if (!ok) return;
        }
        await run(btn, () => deps.client.updateGame(g.gameId, { enabled: next }));
      });
    });

    listEl.querySelectorAll<HTMLButtonElement>('button[data-toggle-maintenance]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const g = byId.get(btn.dataset.toggleMaintenance!);
        if (!g) return;
        const on = g.registryMaintenance.enabled;
        if (on) {
          await run(btn, () => deps.client.updateGame(g.gameId, { maintenance: { enabled: false } }));
          return;
        }
        const message = await promptModal({
          title: `Put ${g.gameId} under maintenance?`,
          body: 'Shown to players across every integrator. Bets are rejected and no new rounds open while maintenance is on; in-flight rounds settle normally.',
          defaultValue: g.registryMaintenance.message ?? 'Game is temporarily under maintenance. Please check back shortly.',
          confirmLabel: 'Start maintenance',
        });
        if (message === null) return;
        await run(btn, () =>
          deps.client.updateGame(g.gameId, { maintenance: { enabled: true, message } }),
        );
      });
    });
  }

  // CSP-safe icon fallback: inline onerror handlers are blocked by the admin
  // CSP, so wire each catalog icon's error in JS. A 404 (game ships no icon)
  // swaps the <img> for a lettered tile.
  function bindIconFallbacks(): void {
    listEl.querySelectorAll<HTMLImageElement>('img.game-card-icon').forEach((img) => {
      const swap = (): void => {
        const fallback = document.createElement('div');
        fallback.className = 'game-card-icon game-card-icon--letter';
        fallback.textContent = (img.dataset.letter ?? '?').toUpperCase();
        img.replaceWith(fallback);
      };
      if (img.complete && img.naturalWidth === 0) swap();
      else img.addEventListener('error', swap, { once: true });
    });
  }

  async function run(btn: HTMLButtonElement, op: () => Promise<unknown>): Promise<void> {
    btn.disabled = true;
    try {
      await op();
      await refresh();
    } catch (err) {
      showToast(describe(err), 'error');
      btn.disabled = false;
    }
  }

  await refresh();
}

// Per-gameId icon art, served by admin-ui itself from `public/game-icons/<id>.png`
// (a copy of the game's own `public/assets/ui/icon.png`). Same-origin, so the URL
// resolves identically in dev and prod — the integrator Catalog page uses the same
// files. The previous URL pointed at the play origin (`/games/<id>/assets/ui/icon.png`),
// which only exists in prod, where Caddy serves every game under one host; in dev each
// game has its own Vite port and serves assets at the root, so every icon 404'd and the
// whole grid fell back to letter tiles. A game we ship no icon for still 404s and
// degrades to its letter tile via bindIconFallbacks().
function gameIconUrl(gameId: string): string {
  return `/game-icons/${gameId.replace(/[^a-z0-9-]/gi, '')}.png`;
}

function card(g: GameCatalogEntry): string {
  const name = prettyGame(g.gameId);
  const letter = name.slice(0, 1);
  const iconHtml = `<img class="game-card-icon" src="${escapeHtml(gameIconUrl(g.gameId))}" alt="${escapeHtml(name)} icon" data-letter="${escapeHtml(letter)}" />`;
  const underMaintenance = g.registryMaintenance.enabled;
  const statusPill = g.registryEnabled
    ? `<span class="pill live">on</span>`
    : `<span class="pill warn">off</span>`;
  const maintPill = underMaintenance ? `<span class="pill maint">maintenance</span>` : '';
  return `<div class="game-card ${g.registryEnabled ? '' : 'is-off'}">
    <div class="game-card-header">
      ${iconHtml}
      <div style="flex:1;min-width:0">
        <div class="row" style="justify-content:space-between;margin:0;gap:8px">
          <h3>${escapeHtml(name)}</h3>
          <div class="pill-stack">${statusPill}${maintPill}</div>
        </div>
        <div class="game-card-meta"><code>${escapeHtml(g.gameId)}</code> · ${escapeHtml(g.type)} · ${escapeHtml(g.scope)}</div>
      </div>
    </div>
    <div class="game-card-actions">
      <button data-toggle-enabled="${escapeHtml(g.gameId)}" class="${g.registryEnabled ? 'ghost' : 'primary'}">
        ${g.registryEnabled ? 'Disable' : 'Enable'}
      </button>
      <button data-toggle-maintenance="${escapeHtml(g.gameId)}" class="ghost">
        ${underMaintenance ? 'End maintenance' : 'Maintenance'}
      </button>
    </div>
    <a href="/games/${encodeURIComponent(g.gameId)}/timing" class="game-card-cta">Configure timing, RNG, jackpot, rules →</a>
  </div>`;
}

const STYLE = `<style>
  .game-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
  }
  .game-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 16px;
    background: var(--panel-2);
    transition: border-color 120ms, opacity 120ms;
  }
  .game-card.is-off {
    opacity: 0.62;
  }
  .game-card.is-off:hover {
    opacity: 1;
  }
  .game-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .game-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    object-fit: cover;
    background: var(--panel);
    flex-shrink: 0;
  }
  .game-card-icon--letter {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 700;
    color: var(--accent-2, var(--text));
    background: color-mix(in srgb, var(--accent-2, var(--text)) 14%, var(--panel));
    border: 1px solid var(--border);
  }
  .game-card h3 {
    margin: 0;
    font-size: 16px;
  }
  .game-card-meta {
    font-size: 11px;
    color: var(--text-dim);
    margin: 4px 0 0;
    font-family: ui-monospace, SF Mono, Menlo, monospace;
  }
  .pill-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    flex-shrink: 0;
  }
  .pill.maint {
    background: color-mix(in srgb, var(--warn) 22%, var(--panel-2));
    color: var(--warn);
  }
  .game-card-actions {
    display: flex;
    gap: 8px;
  }
  .game-card-actions button {
    flex: 1;
    font-size: 13px;
  }
  .game-card-cta {
    font-size: 12px;
    color: var(--accent-2);
    text-decoration: none;
  }
  .game-card-cta:hover {
    text-decoration: underline;
  }
</style>`;

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
